/**
 * Text on user-data colors (status and priority labels, group colors, avatars). The background comes
 * from data, so the text color is chosen at runtime: whichever of the two "on color" tokens has the
 * higher WCAG 2.2 contrast (NFR-7). monday puts white on every label; that fails AA on the light ones.
 */

const ON_LIGHT = 'var(--on-color-dark)';
const ON_DARK = 'var(--on-color-light)';

/** Must match `--on-color-dark` and `--on-color-light` in tokens.css. */
const DARK_TEXT = '#1f1f1f';
const LIGHT_TEXT = '#ffffff';

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.040_45 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of `#rrggbb` (or `#rgb`); undefined for anything else. */
export function relativeLuminance(hex: string): number | undefined {
  const match = /^#([\da-f]{3}|[\da-f]{6})$/i.exec(hex.trim());
  const digits = match?.[1];
  if (!digits) return undefined;
  const full = digits.length === 3 ? digits.replace(/./g, (d) => d + d) : digits;
  const [r, g, b] = [0, 2, 4].map((i) => channel(Number.parseInt(full.slice(i, i + 2), 16)));
  return 0.2126 * (r ?? 0) + 0.7152 * (g ?? 0) + 0.0722 * (b ?? 0);
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a) ?? 0;
  const lb = relativeLuminance(b) ?? 0;
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** CSS color for text on `background`. Unknown formats get the dark text (backgrounds are light). */
export function textColorOn(background: string): string {
  if (relativeLuminance(background) === undefined) return ON_LIGHT;
  return contrastRatio(background, LIGHT_TEXT) >= contrastRatio(background, DARK_TEXT)
    ? ON_DARK
    : ON_LIGHT;
}

/** Must match `--surface` and `--surface-subtle` of each theme in tokens.css (D41). */
export const SURFACES = {
  light: { surface: '#ffffff', subtle: '#f6f6f7' },
  dark: { surface: '#18181b', subtle: '#0f0f11' },
} as const;
export const SURFACE = SURFACES.light.surface;
export const SURFACE_SUBTLE = SURFACES.light.subtle;

function channelsOf(color: string): number[] | undefined {
  const hex = /^#([\da-f]{6})$/i.exec(color.trim())?.[1];
  return hex ? [0, 2, 4].map((i) => Number.parseInt(hex.slice(i, i + 2), 16)) : undefined;
}

function toHex(channels: readonly number[]): string {
  return `#${channels.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
}

/**
 * A data color used as text on a page surface (group names, section titles, Kanban columns): the same
 * hue, darkened on a light surface or lightened on a dark one just enough to reach WCAG AA (4.5:1).
 * Colors that already pass are returned unchanged.
 */
export function readableOnSurface(
  color: string,
  { surface = SURFACE, minimum = 4.5 }: { surface?: string; minimum?: number } = {},
): string {
  const channels = channelsOf(color);
  if (!channels) return color;
  const darken = contrastRatio(surface, '#000000') >= contrastRatio(surface, '#ffffff');
  for (let step = 0; step <= 20; step += 1) {
    const amount = step / 20;
    const candidate = toHex(
      channels.map((c) => (darken ? c * (1 - amount) : c + (255 - c) * amount)),
    );
    if (contrastRatio(candidate, surface) >= minimum) return candidate;
  }
  return darken ? '#000000' : '#ffffff';
}

/**
 * A pale wash of a data color for a tinted pill (D40: priority on the Dashboard): `weight` of the color
 * over the surface it sits on — white by default, the dark surface in dark mode (D41). Pair it with
 * `readableOnSurface(color, { surface: tint })` for the text.
 */
export function tintOf(color: string, weight = 0.14, surface: string = SURFACE): string {
  const channels = channelsOf(color);
  const base = channelsOf(surface);
  if (!channels || !base) return color;
  return toHex(channels.map((c, i) => (base[i] ?? 255) + (c - (base[i] ?? 255)) * weight));
}
